import { useEffect, useState, useRef, useCallback } from "react";
import {
  Database, Plus, Trash2, ChevronUp, ChevronDown, Save, Download, Upload,
  Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle, FileJson, RotateCcw, X as XIcon,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SECTION_LABELS = {
  master_data: "Master Data lists",
  automation_config: "Automation config",
  pipeline_stages: "Pipeline stages",
  pipeline_substages: "Pipeline sub-stages",
  assignment_rules: "Assignment rules",
  qual_flows: "Qualification flows",
  deal_forms: "Deal forms",
};

const buildPreview = (payload, fileName) => {
  const restorable = [], snapshot = [];
  const md = payload.master_data || (payload.lists ? { lists: payload.lists, business_categories: payload.business_categories } : null);
  if (md) {
    const listCount = md.lists ? Object.keys(md.lists).length : 0;
    const segCount = md.business_categories?.segments?.length || 0;
    restorable.push({ key: "master_data", label: SECTION_LABELS.master_data, detail: `${listCount} list(s)${segCount ? ` · ${segCount} segment(s)` : ""}` });
  }
  const cols = payload.collections || {};
  ["automation_config", "pipeline_stages", "pipeline_substages", "assignment_rules"].forEach((k) => {
    if (Array.isArray(cols[k])) restorable.push({ key: k, label: SECTION_LABELS[k], detail: `${cols[k].length} item(s)` });
  });
  ["qual_flows", "deal_forms"].forEach((k) => {
    if (Array.isArray(cols[k]) && cols[k].length) snapshot.push({ key: k, label: SECTION_LABELS[k], detail: `${cols[k].length} item(s)` });
  });
  return { fileName, version: payload.version, exportedAt: payload.exported_at, restorable, snapshot };
};

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

function ItemRow({ item, onChange, onMoveUp, onMoveDown, onDelete, testid }) {
  return (
    <div data-testid={testid} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${item.active ? "border-sky/70 bg-white" : "border-dashed border-sky/60 bg-mist/50 opacity-70"}`}>
      <input
        value={item.value}
        onChange={(e) => onChange({ ...item, value: e.target.value })}
        className="h-8 flex-1 rounded-md border border-transparent bg-transparent px-1.5 text-sm text-navy outline-none focus:border-loop focus:bg-white"
      />
      {item.count > 0 && <span className="shrink-0 rounded-full bg-sky/50 px-2 py-0.5 text-[10px] font-semibold text-navy" title="Leads using this value">{item.count} in use</span>}
      <button onClick={() => onChange({ ...item, active: !item.active })} title={item.active ? "Active — click to hide from dropdowns" : "Hidden — click to activate"} className={`rounded-md p-1.5 ${item.active ? "text-emerald-600 hover:bg-emerald-50" : "text-ink/40 hover:bg-mist"}`}>
        {item.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
      </button>
      <button onClick={onMoveUp} className="rounded-md p-1.5 text-ink/40 hover:bg-mist"><ChevronUp className="h-3.5 w-3.5" /></button>
      <button onClick={onMoveDown} className="rounded-md p-1.5 text-ink/40 hover:bg-mist"><ChevronDown className="h-3.5 w-3.5" /></button>
      <button onClick={onDelete} className="rounded-md p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function ListEditor({ list, hint, onSave, saving }) {
  const [items, setItems] = useState(list.items);
  const [newVal, setNewVal] = useState("");
  const [pendingDel, setPendingDel] = useState(null);
  useEffect(() => { setItems(list.items); }, [list.items]);

  const dirty = JSON.stringify(items) !== JSON.stringify(list.items);
  const setAt = (i, v) => setItems((a) => a.map((x, k) => (k === i ? v : x)));
  const add = () => {
    const v = newVal.trim();
    if (!v) return;
    if (items.some((x) => x.value.toLowerCase() === v.toLowerCase())) return toast.error("That value already exists");
    setItems((a) => [...a, { value: v, active: true, count: 0 }]);
    setNewVal("");
  };
  const delAt = (i) => {
    const it = items[i];
    if (it.count > 0) setPendingDel({ i, value: it.value, count: it.count });
    else setItems((a) => a.filter((_, k) => k !== i));
  };

  return (
    <div className="rounded-xl border border-sky/60 bg-mist/30 p-4" data-testid={`md-list-${list.key}`}>
      <div className="mb-1 flex items-start justify-between gap-2">
        <div>
          <h3 className="font-sora text-sm font-semibold text-navy">{list.label}</h3>
          {hint && <p className="mt-0.5 text-[11px] text-ink/45">Used in: {hint}</p>}
        </div>
        <button
          data-testid={`md-save-${list.key}`}
          disabled={!dirty || saving}
          onClick={() => onSave(list.key, items)}
          className="flex shrink-0 items-center gap-1 rounded-lg bg-loop px-3 py-1.5 text-xs font-semibold text-white hover:bg-loophover disabled:opacity-40">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Save
        </button>
      </div>
      <div className="mt-3 space-y-1.5">
        {items.map((it, i) => (
          <ItemRow
            key={i}
            testid={`md-item-${list.key}-${i}`}
            item={it}
            onChange={(v) => setAt(i, v)}
            onMoveUp={() => setItems((a) => move(a, i, -1))}
            onMoveDown={() => setItems((a) => move(a, i, 1))}
            onDelete={() => delAt(i)}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          data-testid={`md-add-input-${list.key}`}
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a new value…"
          className="h-8 flex-1 rounded-md border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop"
        />
        <button data-testid={`md-add-btn-${list.key}`} onClick={add} className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30"><Plus className="h-3.5 w-3.5" />Add</button>
      </div>
      <ConfirmDialog
        open={!!pendingDel}
        onOpenChange={(v) => !v && setPendingDel(null)}
        title={`Remove "${pendingDel?.value}"?`}
        description={`${pendingDel?.count} active lead(s) still use this value. Removing it only hides it from future dropdowns — existing leads keep their data. Prefer hiding (eye icon) it instead so reports stay intact.`}
        confirmLabel="Remove anyway"
        testid={`md-del-confirm-${list.key}`}
        onConfirm={() => { setItems((a) => a.filter((_, k) => k !== pendingDel.i)); setPendingDel(null); }}
      />
    </div>
  );
}

function CategoriesEditor({ data, onSave, saving }) {
  const [segments, setSegments] = useState(data.segments);
  const [newSeg, setNewSeg] = useState("");
  useEffect(() => { setSegments(data.segments); }, [data.segments]);
  const dirty = JSON.stringify(segments) !== JSON.stringify(data.segments);

  const setSeg = (si, patch) => setSegments((a) => a.map((s, k) => (k === si ? { ...s, ...patch } : s)));
  const setCat = (si, ci, v) => setSeg(si, { categories: segments[si].categories.map((c, k) => (k === ci ? v : c)) });
  const addCat = (si, val) => {
    const v = val.trim();
    if (!v) return;
    if (segments[si].categories.some((c) => c.value.toLowerCase() === v.toLowerCase())) return toast.error("Category exists");
    setSeg(si, { categories: [...segments[si].categories, { value: v, active: true, count: 0 }] });
  };
  const delCat = (si, ci) => setSeg(si, { categories: segments[si].categories.filter((_, k) => k !== ci) });
  const addSeg = () => {
    const v = newSeg.trim();
    if (!v) return;
    if (segments.some((s) => s.segment.toLowerCase() === v.toLowerCase())) return toast.error("Segment exists");
    setSegments((a) => [...a, { segment: v, active: true, count: 0, categories: [] }]);
    setNewSeg("");
  };

  return (
    <div className="rounded-xl border border-sky/60 bg-mist/30 p-4" data-testid="md-categories">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-sora text-sm font-semibold text-navy">Business Segments → Categories</h3>
        <button data-testid="md-save-categories" disabled={!dirty || saving} onClick={() => onSave(segments)} className="flex items-center gap-1 rounded-lg bg-loop px-3 py-1.5 text-xs font-semibold text-white hover:bg-loophover disabled:opacity-40">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Save
        </button>
      </div>
      <div className="space-y-4">
        {segments.map((s, si) => (
          <div key={si} className="rounded-lg border border-sky/60 bg-white p-3" data-testid={`md-segment-${si}`}>
            <div className="mb-2 flex items-center gap-2">
              <input value={s.segment} onChange={(e) => setSeg(si, { segment: e.target.value })} className="h-8 flex-1 rounded-md border border-sky px-2 text-sm font-semibold text-navy outline-none focus:border-loop" />
              {s.count > 0 && <span className="rounded-full bg-sky/50 px-2 py-0.5 text-[10px] font-semibold text-navy">{s.count} in use</span>}
              <button onClick={() => setSeg(si, { active: !s.active })} className={`rounded-md p-1.5 ${s.active ? "text-emerald-600 hover:bg-emerald-50" : "text-ink/40 hover:bg-mist"}`}>{s.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}</button>
              <button onClick={() => setSegments((a) => a.filter((_, k) => k !== si))} className="rounded-md p-1.5 text-red-400 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            <div className="ml-3 flex flex-wrap gap-1.5">
              {s.categories.map((c, ci) => (
                <span key={ci} className={`flex items-center gap-1 rounded-full border px-2 py-1 text-xs ${c.active ? "border-sky bg-mist text-navy" : "border-dashed border-sky/60 text-ink/40"}`}>
                  <input value={c.value} onChange={(e) => setCat(si, ci, { ...c, value: e.target.value })} className="w-24 bg-transparent outline-none" />
                  {c.count > 0 && <span className="text-[9px] text-loop">·{c.count}</span>}
                  <button onClick={() => setCat(si, ci, { ...c, active: !c.active })} className="text-ink/40 hover:text-emerald-600">{c.active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}</button>
                  <button onClick={() => delCat(si, ci)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
              <input
                data-testid={`md-cat-add-${si}`}
                placeholder="+ category"
                onKeyDown={(e) => { if (e.key === "Enter") { addCat(si, e.target.value); e.target.value = ""; } }}
                className="h-7 w-28 rounded-full border border-sky bg-white px-2.5 text-xs outline-none focus:border-loop"
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input data-testid="md-add-segment" value={newSeg} onChange={(e) => setNewSeg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSeg()} placeholder="Add a new segment…" className="h-8 flex-1 rounded-md border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop" />
        <button onClick={addSeg} className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30"><Plus className="h-3.5 w-3.5" />Add segment</button>
      </div>
    </div>
  );
}

const USAGE_HINTS = {
  lead_sources: "Quick Add → manual lead source",
  payment_structures: "Won deal & Change-Stage deal section",
  lost_reasons: "moving a lead to Lost (Change Stage), CRM import. Automation's Lost→Quality rules read from this.",
  junk_reasons: "moving a lead to Junk (Change Stage)",
  activity_types: "Activities, Meetings & Change-Stage activity",
};

export default function MasterDataSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/master-data");
      setData(data.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveList = async (key, items) => {
    setSavingKey(key);
    try {
      await api.put(`/master-data/lists/${key}`, { items: items.map(({ value, active }) => ({ value, active })) });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingKey(""); }
  };

  const saveCategories = async (segments) => {
    setSavingKey("categories");
    try {
      await api.put("/master-data/business-categories", {
        segments: segments.map((s) => ({ segment: s.segment, active: s.active, categories: s.categories.map(({ value, active }) => ({ value, active })) })),
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingKey(""); }
  };

  const exportBackup = async () => {
    try {
      const res = await api.get("/master-data/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = "acquireloop_workspace_backup.json";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Workspace backup downloaded");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const restorePreviewFromFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const valid = payload && (payload.master_data || payload.collections || payload.lists || payload.business_categories);
      if (!valid) return toast.error("This file is not a valid AcquireLoop backup");
      setRestorePreview({ payload, ...buildPreview(payload, file.name) });
    } catch (e) {
      toast.error(e?.name === "SyntaxError" ? "Invalid backup file (not valid JSON)" : apiError(e));
    }
  };

  const confirmRestore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/master-data/restore", restorePreview.payload);
      setRestoreResult(data.data);
      setRestorePreview(null);
      toast.success("Workspace restored successfully");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-6" data-testid="master-data-settings">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-sora font-semibold text-navy"><Database className="h-5 w-5 text-loop" />Master Data</h2>
        <div className="flex items-center gap-2">
          <button data-testid="md-export-btn" onClick={exportBackup} className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30"><Download className="h-3.5 w-3.5" />Workspace Backup</button>
          <button data-testid="md-restore-btn" onClick={() => fileRef.current?.click()} className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 text-xs font-semibold text-navy hover:bg-mist"><Upload className="h-3.5 w-3.5" />Restore</button>
          <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" data-testid="md-restore-input" onChange={(e) => { restorePreviewFromFile(e.target.files[0]); if (fileRef.current) fileRef.current.value = ""; }} />
        </div>
      </div>
      <p className="mb-3 text-xs text-ink/55">Configure the dropdown values used across the app. Hiding a value (eye icon) removes it from new dropdowns without changing existing leads. <b>Workspace Backup</b> downloads your full configuration (master data, automation, qualification flows, deal forms, pipeline & assignment rules); Restore safely re-applies master data, automation, pipeline & assignment rules (flows/deal forms are kept as a downloadable snapshot only). <span className="text-ink/45">Services & Budget Ranges are managed inside your Qualification flows.</span></p>

      {/* Last-backup reminder */}
      {data && (
        <div data-testid="md-backup-status" className={`mb-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${data.last_backup_at ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
          {data.last_backup_at ? (
            <><CheckCircle2 className="h-3.5 w-3.5 shrink-0" />Last backed up <b>{fmtRelative(data.last_backup_at)}</b>{data.last_backup_by ? <> by {data.last_backup_by}</> : null}.<button onClick={exportBackup} className="ml-1 font-semibold underline hover:no-underline">Back up again</button></>
          ) : (
            <><AlertTriangle className="h-3.5 w-3.5 shrink-0" />You haven't backed up yet. <button onClick={exportBackup} className="font-semibold underline hover:no-underline">Download a Workspace Backup</button> before making big changes.</>
          )}
        </div>
      )}

      {/* Restore success banner */}
      {restoreResult && (
        <div data-testid="md-restore-result" className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Restore complete.</p>
            <p className="mt-0.5">Re-applied: {(restoreResult.restored || []).map((k) => SECTION_LABELS[k] || k).join(", ") || "nothing"}.</p>
            {restoreResult.skipped?.length > 0 && (
              <p className="mt-0.5 text-emerald-700/80">Kept unchanged (export-only snapshot): {restoreResult.skipped.map((k) => SECTION_LABELS[k] || k).join(", ")}.</p>
            )}
          </div>
          <button onClick={() => setRestoreResult(null)} className="text-emerald-600 hover:text-emerald-800"><XIcon className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-ink/50"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
      ) : !data ? (
        <p className="py-6 text-sm text-ink/50">Could not load master data.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {data.lists.map((l) => (
            <ListEditor key={l.key} list={l} hint={USAGE_HINTS[l.key]} onSave={saveList} saving={savingKey === l.key} />
          ))}
          <div className="lg:col-span-2">
            <CategoriesEditor data={data.business_categories} onSave={saveCategories} saving={savingKey === "categories"} />
          </div>
        </div>
      )}

      {/* Restore preview / confirmation */}
      <AlertDialog open={!!restorePreview} onOpenChange={(v) => !restoring && !v && setRestorePreview(null)}>
        <AlertDialogContent className="border-sky bg-white" data-testid="md-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-sora text-navy"><FileJson className="h-5 w-5 text-loop" />Restore from backup</AlertDialogTitle>
          </AlertDialogHeader>
          {restorePreview && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-mist px-3 py-2 text-xs text-ink/60">
                <span className="font-medium text-navy">{restorePreview.fileName}</span>
                {restorePreview.exportedAt ? <> · created {fmtRelative(restorePreview.exportedAt)}</> : null}
                {restorePreview.version ? <> · v{restorePreview.version}</> : null}
              </div>

              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-navy"><RotateCcw className="h-3.5 w-3.5 text-loop" />Will be re-applied (current settings replaced):</p>
                <ul className="space-y-1">
                  {restorePreview.restorable.map((s) => (
                    <li key={s.key} className="flex items-center justify-between rounded-md border border-sky/60 bg-white px-2.5 py-1.5 text-xs" data-testid={`restore-apply-${s.key}`}>
                      <span className="text-ink/80">{s.label}</span><span className="text-ink/45">{s.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {restorePreview.snapshot.length > 0 && (
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink/50">Kept as snapshot only (your live data is NOT changed):</p>
                  <ul className="space-y-1">
                    {restorePreview.snapshot.map((s) => (
                      <li key={s.key} className="flex items-center justify-between rounded-md border border-dashed border-sky/60 bg-mist/40 px-2.5 py-1.5 text-xs text-ink/55" data-testid={`restore-skip-${s.key}`}>
                        <span>{s.label}</span><span className="text-ink/40">{s.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />This replaces your current Master Data, automation, pipeline & assignment settings with the backup. Existing leads keep their data. Consider downloading a fresh backup first.
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <button data-testid="md-restore-cancel" onClick={() => setRestorePreview(null)} disabled={restoring} className="rounded-xl border border-sky px-4 py-2 text-sm font-medium text-ink/70 hover:bg-mist disabled:opacity-50">Cancel</button>
            <button data-testid="md-restore-confirm" onClick={confirmRestore} disabled={restoring} className="flex items-center justify-center gap-2 rounded-xl bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
              {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}Restore now
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
